import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Icon } from '~/components';
import { CopyButton } from '~/components/CopyButton/CopyButton';
import { Spinner } from '~/components/Spinner';
import { cn } from '~/lib/cn';

import { getArtistContactInfo } from '../../lib/artists';

interface ArtistContactInfoProps {
  partyId: number;
}

type InfoType = 'artist' | 'songwriter';

export function ArtistContactInfo({ partyId }: ArtistContactInfoProps) {
  const { t } = useTranslation('roster');

  const { data, isLoading } = useQuery({
    queryKey: ['artist-contact-info', partyId],
    queryFn: () => getArtistContactInfo({ partyId }),
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
  });

  const [selectedInfoType, setSelectedInfoType] = useState<InfoType>('artist');
  const artistContacts = data?.artistContacts;
  const songwriterContacts = data?.songwriterContacts;
  const contacts =
    selectedInfoType === 'artist' || !songwriterContacts
      ? artistContacts
      : songwriterContacts;

  if (isLoading || !contacts) {
    return (
      <div className="rounded-sm border p-md bg-level-03 border-emphasis-strong">
        <Spinner />
      </div>
    );
  }

  const isMultiple = contacts.length > 1;

  return (
    <div className="flex flex-col gap-lg rounded-sm border p-md bg-level-03 border-emphasis-strong text-white">
      {songwriterContacts?.length ? (
        <div className="flex w-full items-center justify-start gap-md pb-xs bg-transparent text-default">
          <button
            className={cn('underline-offset-8 hover:underline', {
              'underline decoration-2 decoration-primary-default':
                selectedInfoType === 'artist',
            })}
            onClick={() => setSelectedInfoType('artist')}
          >
            {t('contactInfo.artistInfo')}
          </button>
          <button
            className={cn('underline-offset-8 hover:underline', {
              'underline decoration-2 decoration-primary-default':
                selectedInfoType === 'songwriter',
            })}
            onClick={() => setSelectedInfoType('songwriter')}
          >
            {t('contactInfo.songwriterInfo')}
          </button>
        </div>
      ) : null}
      {contacts.length ? (
        <div
          className={isMultiple ? 'grid grid-cols-2 gap-md' : 'inline-block'}
        >
          {contacts.map((contact) => (
            <div
              className="flex flex-col items-start gap-lg rounded-sm p-md bg-level-04"
              key={`${contact.firstName} + ${contact.lastName}`}
            >
              <div className="flex flex-col">
                <span className="text-body-xs font-bold text-white">
                  {contact.title}
                </span>
                <span className="text-body-md">
                  {t('contactInfo.fullName', {
                    firstName: contact.firstName,
                    lastName: contact.lastName,
                  })}
                </span>
              </div>
              <div className="grid w-full grid-cols-[auto,1fr,auto] gap-0 text-body-sm">
                {contact.phone ? (
                  <div className="group contents">
                    <div className="flex items-center pr-sm">
                      <Icon type="mobile" variant="light" aria-hidden />
                    </div>
                    <a
                      href={`tel:${contact.phone.replace(/[^\d+]/g, '')}`}
                      className="flex items-center pr-sm underline-offset-2 group-hover:underline"
                    >
                      {contact.phone}
                    </a>
                    <div className="flex items-center">
                      <CopyButton
                        textToCopy={contact.phone}
                        tooltipContent={t('contactInfo.copyNumber')}
                        tooltipContentCopied={t('contactInfo.copyNumberCopied')}
                        className="opacity-none focus:opacity-full group-hover:opacity-full"
                      />
                    </div>
                  </div>
                ) : null}
                {contact.email ? (
                  <div className="group contents">
                    <div className="flex items-center pr-sm">
                      <Icon type="envelope" variant="light" aria-hidden />
                    </div>
                    <a
                      href={`mailto:${contact.email}`}
                      className="flex items-center pr-sm underline-offset-2 group-hover:underline"
                    >
                      {contact.email}
                    </a>
                    <div className="flex items-center">
                      <CopyButton
                        textToCopy={contact.email}
                        tooltipContent={t('contactInfo.copyEmail')}
                        tooltipContentCopied={t('contactInfo.copyEmailCopied')}
                        className="opacity-none focus:opacity-full group-hover:opacity-full"
                      />
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

ArtistContactInfo.displayName = 'ArtistContactInfo';
